import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { menuAPI } from '../services/api';
import { customToast, toastMessages } from '../utils/toast';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useLanguage } from '../contexts/LanguageContext';
import FeatureGate from '../components/FeatureGate';
import {
  Plus,
  Edit,
  Trash2,
  Utensils,
  Tag,
  Sparkles,
  Search,
  Filter,
  Image as ImageIcon,
  DollarSign,
  CheckCircle,
  XCircle,
  AlertTriangle,
  X,
  GripVertical
} from 'lucide-react';

const MenuManagement = () => {
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showDishModal, setShowDishModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingDish, setEditingDish] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [categoryOrder, setCategoryOrder] = useState([]);
  const [orderedCategories, setOrderedCategories] = useState([]);
  const [isReordering, setIsReordering] = useState(false);
  const [saveStatus, setSaveStatus] = useState('saved');
  const { isFeatureAvailable } = useSubscription();
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const { t } = useLanguage(); // Add language hook

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const queryClient = useQueryClient();

  // Fetch categories
  const { data: categoriesData, isLoading: categoriesLoading } = useQuery(
    'categories',
    () => menuAPI.getCategories()
  );

  // Fetch dishes
  const { data: dishesData, isLoading: dishesLoading } = useQuery(
    'dishes',
    () => menuAPI.getDishes()
  );

  const categories = categoriesData?.data || [];
  const dishes = dishesData?.data || [];


  // Initialize orderedCategories when categories are loaded and fetch saved order
  useEffect(() => {
    const initializeCategories = async () => {
      if (categories.length > 0) {
        try {
          console.log('Fetching saved order...');
          const response = await menuAPI.getCategoryOrder();
          console.log('Get order response:', response);

          // Check different possible response structures
          const savedOrder = response?.category_order ||
            response?.data?.category_order ||
            [];

          console.log('Saved order IDs:', savedOrder);

          if (savedOrder.length > 0) {
            const ordered = savedOrder
              .map(id => categories.find(cat => cat.id === id))
              .filter(cat => cat);

            const remainingCategories = categories.filter(
              cat => !savedOrder.includes(cat.id)
            );

            const newOrderedCategories = [...ordered, ...remainingCategories];
            console.log('Setting ordered categories:', newOrderedCategories);
            setOrderedCategories(newOrderedCategories);
          } else {
            setOrderedCategories(categories);
          }
        } catch (error) {
          console.error('Error fetching order:', error);
          setOrderedCategories(categories);
        }
      }
    };

    initializeCategories();
  }, [categories]);

  // Filter dishes based on category and search
  const filteredDishes = dishes.filter(dish => {
    const matchesCategory = !selectedCategory || dish.category_id === selectedCategory;
    const matchesSearch = dish.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dish.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Category mutations
  const createCategoryMutation = useMutation(
    (data) => menuAPI.createCategory(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('categories');
        customToast.success(toastMessages.categoryCreated);
        setShowCategoryModal(false);
      },
      onError: (error) => {
        customToast.error(toastMessages.createError('category'));
      },
    }
  );

  const updateCategoryMutation = useMutation(
    (data) => menuAPI.updateCategory(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('categories');
        customToast.success(toastMessages.categoryUpdated);
        setShowCategoryModal(false);
        setEditingCategory(null);
      },
      onError: (error) => {
        customToast.error(toastMessages.updateError('category'));
      },
    }
  );

  const deleteCategoryMutation = useMutation(
    (data) => menuAPI.deleteCategory(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('categories');
        queryClient.invalidateQueries('dishes');
        customToast.success(toastMessages.categoryDeleted);
        setShowDeleteConfirm(false);
        setItemToDelete(null);
      },
      onError: (error) => {
        customToast.error(toastMessages.deleteError('category'));
        setShowDeleteConfirm(false);
        setItemToDelete(null);
      },
    }
  );

  // Dish mutations
  const createDishMutation = useMutation(
    (data) => menuAPI.createDish(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('dishes');
        customToast.success(toastMessages.dishCreated);
        setShowDishModal(false);
      },
      onError: (error) => {
        customToast.error(toastMessages.createError('dish'));
      },
    }
  );

  const updateDishMutation = useMutation(
    (data) => menuAPI.updateDish(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('dishes');
        customToast.success(toastMessages.dishUpdated);
        setShowDishModal(false);
        setEditingDish(null);
      },
      onError: (error) => {
        customToast.error(toastMessages.updateError('dish'));
      },
    }
  );

  const deleteDishMutation = useMutation(
    (data) => menuAPI.deleteDish(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('dishes');
        customToast.success(toastMessages.dishDeleted);
        setShowDeleteConfirm(false);
        setItemToDelete(null);
      },
      onError: (error) => {
        customToast.error(toastMessages.deleteError('dish'));
        setShowDeleteConfirm(false);
        setItemToDelete(null);
      },
    }
  );

  const handleCreateCategory = (data) => {
    createCategoryMutation.mutate(data);
  };

  const handleUpdateCategory = (data) => {
    updateCategoryMutation.mutate(data);
  };

  const handleDeleteCategory = (category) => {
    setItemToDelete(category);
    setDeleteType('category');
    setShowDeleteConfirm(true);
  };

  const handleCreateDish = (data) => {
    createDishMutation.mutate(data);
  };

  const handleUpdateDish = (data) => {
    updateDishMutation.mutate(data);
  };

  const handleDeleteDish = (dish) => {
    setItemToDelete(dish);
    setDeleteType('dish');
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (deleteType === 'category' && itemToDelete) {
      deleteCategoryMutation.mutate({ id: itemToDelete.id });
    } else if (deleteType === 'dish' && itemToDelete) {
      deleteDishMutation.mutate({ id: itemToDelete.id });
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setItemToDelete(null);
    setDeleteType('');
  };

  const getCategoryDishCount = (categoryId) => {
    return dishes.filter(dish => dish.category_id === categoryId).length;
  };

  const saveCategoryOrderToServer = async (orderedCategoriesList) => {
    setSaveStatus('saving');

    try {
      const categoryIds = orderedCategoriesList.map(cat => cat.id);
      console.log('Sending category order:', categoryIds);

      const response = await menuAPI.saveCategoryOrder(categoryIds);
      console.log('Server response:', response);

      if (response && response.success) {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(''), 2000);
      } else {
        throw new Error(response?.error || 'Failed to save');
      }
    } catch (error) {
      console.error('Error saving category order:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };


  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
    // Required for Firefox
    e.dataTransfer.setData('text/plain', index);
    // Add a class to the dragged element
    e.target.classList.add('opacity-50', 'bg-blue-50');
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverItem(index);
    // Add visual indicator for drop target
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverItem(null);
    e.target.classList.remove('bg-blue-50', 'border-blue-300');
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();

    // Get the dragged item index
    const dragIndex = draggedItem;

    if (dragIndex === null || dragIndex === dropIndex) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    // Reorder the categories
    const newCategories = [...orderedCategories];
    const temp = newCategories[dragIndex];
    newCategories[dragIndex] = newCategories[dropIndex];
    newCategories[dropIndex] = temp;

    // Update state
    setOrderedCategories(newCategories);
    saveCategoryOrderToServer(newCategories);

    // Reset drag states
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleDragEnd = (e) => {
    e.preventDefault();
    // Remove all drag-related classes
    e.target.classList.remove('opacity-50', 'bg-blue-50');
    setDraggedItem(null);
    setDragOverItem(null);
  };

  // Function to handle image URL validation and display
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;

    // Check if it's a valid URL
    try {
      new URL(imageUrl);
      return imageUrl;
    } catch {
      return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 p-7">
      {/* Animated Background */}
      <div className="absolute inset-0 -z-10 h-full w-full bg-white bg-[linear-gradient(to_right,#f0f0f0_1px,transparent_1px),linear-gradient(to_bottom,#f0f0f0_1px,transparent_1px)] bg-[size:6rem_4rem]">
        <div className="absolute bottom-0 left-0 right-0 top-0 bg-[radial-gradient(circle_800px_at_50%_100px,#f0f9ff,transparent)]"></div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className={`mb-8 transition-all duration-700 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
          <div className="flex items-center gap-3 mb-2">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
              <Sparkles className="h-4 w-4" />
              {t('menuManagement.title', 'Menu Management')}
            </div>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            {t('menuManagement.heading', 'Manage Your Menu')}
          </h1>
          <p className="text-gray-600 text-base md:text-lg max-w-2xl">
            {t('menuManagement.description', 'Organize your restaurant\'s menu with categories and dishes. Create, edit, and manage your offerings.')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 md:gap-8">
          {/* Categories Sidebar */}
          <div className="lg:col-span-1">
            <div className={`bg-white rounded-2xl shadow-lg border border-gray-100 transition-all duration-700 delay-200 ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
              }`}>
              <div className="p-4 md:p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg md:text-xl font-bold text-gray-900">
                    {t('menuManagement.categories', 'Categories')}
                  </h2>
                  <button
                    onClick={() => {
                      setEditingCategory(null);
                      setShowCategoryModal(true);
                    }}
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-2 rounded-xl hover:shadow-lg transition-all duration-300 hover:scale-105"
                  >
                    <Plus className="h-4 w-4 md:h-5 md:w-5" />
                  </button>
                </div>
              </div>

              <div className="p-3 md:p-4">
                {categoriesLoading ? (
                  <div className="flex justify-center py-6 md:py-8">
                    <div className="w-6 h-6 md:w-8 md:h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <button
                      onClick={() => setSelectedCategory(null)}
                      className={`w-full flex items-center justify-between p-3 md:p-4 rounded-xl transition-all duration-300 ${selectedCategory === null
                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                        : 'hover:bg-gray-50 border border-transparent hover:border-gray-200'
                        }`}
                    >
                      <div className="flex items-center gap-2 md:gap-3">
                        <div className="w-6 h-6 md:w-8 md:h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Utensils className="h-3 w-3 md:h-4 md:w-4 text-blue-600" />
                        </div>
                        <span className="font-medium text-sm md:text-base">
                          {t('menuManagement.allDishes', 'All Dishes')}
                        </span>
                      </div>
                      <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 md:px-2 md:py-1 rounded-full text-xs font-medium">
                        {dishes.length}
                      </span>
                    </button>
                    {orderedCategories.map((category, index) => (
                      <div
                        key={category.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                        className={`group p-3 md:p-4 rounded-xl transition-all duration-300 cursor-move ${dragOverItem === index ? 'border-2 border-blue-400 bg-blue-50/50' : ''
                          } ${selectedCategory === category.id
                            ? 'bg-blue-50 border border-blue-200'
                            : 'hover:bg-gray-50 border border-transparent hover:border-gray-200'
                          }`}
                      >
                        <div className="flex items-center justify-between">
                          <button
                            onClick={() => setSelectedCategory(category.id)}
                            className="flex-1 text-left"
                          >
                            <div className="flex items-center gap-2 md:gap-3">
                              {/* Drag handle with GripVertical icon */}
                              <div className="w-6 h-6 md:w-8 md:h-8 bg-gray-100 rounded-lg flex items-center justify-center cursor-move hover:bg-gray-200 transition-colors group/drag">
                                <GripVertical className="h-4 w-4 text-gray-500 group-hover/drag:text-gray-700" />
                              </div>

                              {/* Category icon */}
                              <div className="w-6 h-6 md:w-8 md:h-8 bg-green-100 rounded-lg flex items-center justify-center">
                                <Tag className="h-3 w-3 md:h-4 md:w-4 text-green-600" />
                              </div>

                              {/* Category info */}
                              <div>
                                <div className="font-medium text-gray-900 text-sm md:text-base">{category.name}</div>
                                <div className="text-xs text-gray-500">
                                  {getCategoryDishCount(category.id)} {t('menuManagement.dishes', 'dishes')}
                                </div>
                              </div>
                            </div>
                          </button>

                          {/* Edit/Delete buttons */}
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-1">
                            <button
                              onClick={() => {
                                setEditingCategory(category);
                                setShowCategoryModal(true);
                              }}
                              className="p-1 hover:bg-white rounded-lg transition-colors duration-300"
                              title="Edit category"
                            >
                              <Edit className="h-3 w-3 md:h-4 md:w-4 text-gray-400 hover:text-blue-600" />
                            </button>
                            <button
                              onClick={() => handleDeleteCategory(category)}
                              className="p-1 hover:bg-red-50 rounded-lg transition-colors duration-300"
                              title="Delete category"
                            >
                              <Trash2 className="h-3 w-3 md:h-4 md:w-4 text-gray-400 hover:text-red-600" />
                            </button>
                          </div>
                        </div>

                        {/* Drag instruction tooltip */}
                        <div className="absolute left-0 right-0 bottom-full mb-2 hidden group-hover:block">
                          <div className="bg-gray-800 text-white text-xs rounded py-1 px-2 text-center mx-auto w-max shadow-lg">
                            Drag to reorder
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Menu Stats */}
            <div className={`bg-white rounded-2xl shadow-lg border border-gray-100 p-4 md:p-6 mt-4 md:mt-6 transition-all duration-700 delay-300 ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
              }`}>
              <h3 className="font-bold text-gray-900 mb-3 md:mb-4">
                {t('menuManagement.menuOverview', 'Menu Overview')}
              </h3>
              <div className="space-y-3 md:space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs md:text-sm text-gray-600">
                    {t('menuManagement.totalCategories', 'Total Categories')}
                  </span>
                  <span className="font-semibold text-gray-900 text-sm md:text-base">{categories.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs md:text-sm text-gray-600">
                    {t('menuManagement.totalDishes', 'Total Dishes')}
                  </span>
                  <span className="font-semibold text-gray-900 text-sm md:text-base">{dishes.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs md:text-sm text-gray-600">
                    {t('menuManagement.availableDishes', 'Available Dishes')}
                  </span>
                  <span className="font-semibold text-green-600 text-sm md:text-base">
                    {dishes.filter(d => d.available).length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs md:text-sm text-gray-600">
                    {t('menuManagement.averagePrice', 'Average Price')}
                  </span>
                  <span className="font-semibold text-gray-900 text-sm md:text-base">
                    ${(dishes.reduce((sum, dish) => sum + parseFloat(dish.price), 0) / dishes.length || 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Dishes Main Area */}
          <div className="lg:col-span-3">
            <div className={`bg-white rounded-2xl shadow-lg border border-gray-100 transition-all duration-700 delay-400 ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
              }`}>
              <div className="p-4 md:p-6 border-b border-gray-100">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div>
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                      {selectedCategory
                        ? categories.find(c => c.id === selectedCategory)?.name + ' ' + t('menuManagement.dishes', 'Dishes')
                        : t('menuManagement.allDishes', 'All Dishes')
                      }
                    </h2>
                    <p className="text-gray-600 mt-1 text-sm md:text-base">
                      {filteredDishes.length} {filteredDishes.length === 1
                        ? t('menuManagement.dishFound', 'dish found')
                        : t('menuManagement.dishesFound', 'dishes found')
                      }
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                    {/* Search */}
                    <div className="relative flex-1 min-w-[200px]">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder={t('menuManagement.searchDishes', 'Search dishes...')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                      />
                    </div>

                    <div className="flex items-center gap-3">
                      <button className="flex items-center gap-2 bg-gray-50 px-3 md:px-4 py-2 rounded-xl border border-gray-200 hover:bg-gray-100 transition-all duration-300">
                        <Filter className="h-4 w-4 text-gray-600" />
                        <span className="text-sm font-medium hidden sm:inline">
                          {t('menuManagement.filter', 'Filter')}
                        </span>
                      </button>

                      <button
                        onClick={() => {
                          setEditingDish(null);
                          setShowDishModal(true);
                        }}
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 md:px-6 py-2.5 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center gap-2 whitespace-nowrap"
                      >
                        <Plus className="h-4 w-4 md:h-5 md:w-5" />
                        <span className="text-sm md:text-base">
                          {t('menuManagement.addDish', 'Add Dish')}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 md:p-6">
                {dishesLoading ? (
                  <div className="flex justify-center py-8 md:py-12">
                    <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : filteredDishes.length === 0 ? (
                  <div className="text-center py-12 md:py-16">
                    <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4 md:mb-6">
                      <Utensils className="h-8 w-8 md:h-10 md:w-10 text-blue-600" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 md:mb-3">
                      {t('menuManagement.noDishesFound', 'No Dishes Found')}
                    </h3>
                    <p className="text-gray-600 mb-6 md:mb-8 max-w-md mx-auto text-sm md:text-base">
                      {searchTerm || selectedCategory
                        ? t('menuManagement.noDishesMatch', 'No dishes match your current filters. Try adjusting your search or category selection.')
                        : t('menuManagement.getStarted', 'Get started by creating your first dish to build your menu.')
                      }
                    </p>
                    {!searchTerm && (
                      <button
                        onClick={() => {
                          setEditingDish(null);
                          setShowDishModal(true);
                        }}
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 md:px-8 py-2.5 md:py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center gap-2 mx-auto text-sm md:text-base"
                      >
                        <Plus className="h-4 w-4 md:h-5 md:w-5" />
                        {t('menuManagement.createFirstDish', 'Create Your First Dish')}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
                    {filteredDishes.map((dish) => {
                      const imageUrl = getImageUrl(dish.image_url);
                      return (
                        <div
                          key={dish.id}
                          className="group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-1 md:hover:-translate-y-2 border border-gray-100"
                        >
                          <div className="p-4 md:p-6">
                            <div className="flex items-start gap-4 mb-4">
                              {/* Larger Dish Image/Placeholder */}
                              <div className="flex-shrink-0">
                                {imageUrl ? (
                                  <img
                                    src={imageUrl}
                                    alt={dish.name}
                                    className="w-20 h-20 md:w-28 md:h-28 object-cover rounded-xl"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      // Show placeholder on error
                                      const placeholder = e.target.parentElement.querySelector('.image-placeholder');
                                      if (placeholder) placeholder.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                {!imageUrl && (
                                  <div className="image-placeholder w-24 h-24 md:w-28 md:h-28 bg-gradient-to-br from-gray-200 to-gray-300 rounded-xl flex items-center justify-center">
                                    <Utensils className="h-8 w-8 md:h-10 md:w-10 text-gray-400" />
                                  </div>
                                )}
                              </div>

                              {/* Dish Header */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex-1 min-w-0">
                                    <h3 className="font-bold text-gray-900 mb-1">{dish.name}</h3>
                                    {dish.description && (
                                      <p className="text-gray-600 text-sm line-clamp-2">{dish.description}</p>
                                    )}
                                  </div>
                                </div>

                                {/* Dish Details */}
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-1 text-lg md:text-xl font-bold text-green-600">
                                      CHF {parseFloat(dish.price).toFixed(1)}
                                    </div>
                                    <div className={`flex items-center gap-1 px-1 py-1 rounded-full text-xs font-medium ${dish.available
                                      ? 'bg-green-100 text-green-800 border-green-100'
                                      : 'bg-red-100 text-red-800 border-red-100'
                                      }`}>
                                      {dish.available ? (
                                        <>
                                          <CheckCircle className="h-3 w-3" />
                                          <span className="hidden xs:inline">
                                            {t('menuManagement.available', 'Available')}
                                          </span>
                                        </>
                                      ) : (
                                        <>
                                          <XCircle className="h-3 w-3" />
                                          <span className="hidden xs:inline">
                                            {t('menuManagement.unavailable', 'Unavailable')}
                                          </span>
                                        </>
                                      )}
                                    </div>
                                  </div>

                                  {dish.category_name && (
                                    <div className="flex items-center gap-2 text-sm text-gray-500">
                                      <Tag className="h-3 w-3" />
                                      <span className="truncate">{dish.category_name}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-2 pt-3 border-t border-gray-100">
                              <button
                                onClick={() => {
                                  setEditingDish(dish);
                                  setShowDishModal(true);
                                }}
                                className="flex-1 py-2 px-3 bg-blue-50 text-blue-700 rounded-lg font-medium hover:bg-blue-100 transition-all duration-300 flex items-center justify-center gap-1 border border-blue-200 text-sm"
                              >
                                <Edit className="h-4 w-4" />
                                <span>{t('menuManagement.edit', 'Edit')}</span>
                              </button>
                              <button
                                onClick={() => handleDeleteDish(dish)}
                                className="flex-1 w-4 py-1 px-1 bg-red-50 text-red-700 rounded-lg font-medium hover:bg-red-100 transition-all duration-300 flex items-center justify-center gap-1 border border-red-200 text-sm"
                              >
                                <Trash2 className="h-4 w-4" />
                                {/* <span>{t('menuManagement.delete', 'Delete')}</span> */}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Category Modal */}
        {showCategoryModal && (
          <CategoryModal
            category={editingCategory}
            onClose={() => {
              setShowCategoryModal(false);
              setEditingCategory(null);
            }}
            onSubmit={editingCategory ? handleUpdateCategory : handleCreateCategory}
            isLoading={createCategoryMutation.isLoading || updateCategoryMutation.isLoading}
            t={t} // Pass translation function
          />
        )}

        {/* Dish Modal */}
        {showDishModal && (
          <DishModal
            dish={editingDish}
            categories={categories}
            onClose={() => {
              setShowDishModal(false);
              setEditingDish(null);
            }}
            onSubmit={editingDish ? handleUpdateDish : handleCreateDish}
            isLoading={createDishMutation.isLoading || updateDishMutation.isLoading}
            t={t} // Pass translation function
          />
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && itemToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 md:p-8 max-w-md w-full shadow-2xl border border-gray-100">
              <div className="text-center mb-4 md:mb-6">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center text-white mx-auto mb-3 md:mb-4">
                  <AlertTriangle className="h-6 w-6 md:h-8 md:w-8" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 md:mb-3">
                  {deleteType === 'category'
                    ? t('menuManagement.deleteCategory', 'Delete Category?')
                    : t('menuManagement.deleteDish', 'Delete Dish?')
                  }
                </h3>
                <p className="text-gray-600 mb-2 text-sm md:text-base">
                  {t('menuManagement.deleteConfirm', 'Are you sure you want to delete')} <strong>"{itemToDelete.name}"</strong>?
                </p>
                {deleteType === 'category' && (
                  <p className="text-xs md:text-sm text-red-600 font-medium">
                    {t('menuManagement.deleteCategoryWarning', 'This will also delete')} {getCategoryDishCount(itemToDelete.id)} {t('menuManagement.dishesInCategory', 'dishes in this category.')}
                  </p>
                )}
                <p className="text-xs md:text-sm text-red-600 font-medium mt-2">
                  {t('menuManagement.actionCannotUndone', 'This action cannot be undone.')}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                <button
                  onClick={cancelDelete}
                  className="flex-1 py-2.5 md:py-3 px-4 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all duration-300 border border-gray-200 text-sm md:text-base"
                >
                  {t('menuManagement.cancel', 'Cancel')}
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={deleteCategoryMutation.isLoading || deleteDishMutation.isLoading}
                  className="flex-1 py-2.5 md:py-3 px-4 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2 text-sm md:text-base"
                >
                  {(deleteCategoryMutation.isLoading || deleteDishMutation.isLoading) ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {t('menuManagement.deleting', 'Deleting...')}
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 md:h-5 md:w-5" />
                      {deleteType === 'category'
                        ? t('menuManagement.deleteCategory', 'Delete Category')
                        : t('menuManagement.deleteDish', 'Delete Dish')
                      }
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Category Modal Component
const CategoryModal = ({ category, onClose, onSubmit, isLoading, t }) => {
  const [formData, setFormData] = useState({
    name: category?.name || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = { ...formData };
    if (category) {
      data.id = category.id;
    }
    onSubmit(data);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 md:p-8 w-full max-w-md shadow-2xl border border-gray-100">
        <div className="text-center mb-4 md:mb-6">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center text-white mx-auto mb-3 md:mb-4">
            <Tag className="h-5 w-5 md:h-6 md:w-6" />
          </div>
          <h3 className="text-xl md:text-2xl font-bold text-gray-900">
            {category
              ? t('menuManagement.editCategory', 'Edit Category')
              : t('menuManagement.createCategory', 'Create Category')
            }
          </h3>
          <p className="text-gray-600 mt-2 text-sm md:text-base">
            {category
              ? t('menuManagement.updateCategoryDescription', 'Update your category details')
              : t('menuManagement.createCategoryDescription', 'Add a new category to organize your menu')
            }
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4 md:mb-6">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2 md:mb-3">
              {t('menuManagement.categoryName', 'Category Name')} *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="block w-full px-3 md:px-4 py-2.5 md:py-3 bg-white border border-gray-200 rounded-xl placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 hover:border-gray-300 text-sm md:text-base"
              placeholder={t('menuManagement.categoryPlaceholder', 'e.g., Appetizers, Main Courses, Desserts')}
              required
            />
          </div>

          <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 md:px-6 py-2.5 md:py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all duration-300 border border-gray-200 text-sm md:text-base"
            >
              {t('menuManagement.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 md:px-6 py-2.5 md:py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2 text-sm md:text-base"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {category
                    ? t('menuManagement.updating', 'Updating...')
                    : t('menuManagement.creating', 'Creating...')
                  }
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  {category
                    ? t('menuManagement.updateCategory', 'Update Category')
                    : t('menuManagement.createCategory', 'Create Category')
                  }
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Dish Modal Component with Image Upload - RESPONSIVE VERSION
const DishModal = ({ dish, categories, onClose, onSubmit, isLoading, t }) => {
  const [formData, setFormData] = useState({
    name: dish?.name || '',
    description: dish?.description || '',
    price: dish?.price || '',
    category_id: dish?.category_id || '',
    image_url: dish?.image_url || '',
    available: dish?.available !== undefined ? dish.available : true
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(dish?.image_url || '');
  const [imageError, setImageError] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    let finalImageUrl = formData.image_url;

    // If there's a new image file, upload it first
    if (imageFile) {
      setUploading(true);
      try {
        const response = await menuAPI.uploadImage(imageFile);
        finalImageUrl = response.url || response.image_url || response.data?.url;
      } catch (error) {
        customToast.error(toastMessages.imageUploadError);
        setUploading(false);
        return;
      }
      setUploading(false);
    }

    const data = {
      ...formData,
      price: parseFloat(formData.price),
      category_id: formData.category_id || null,
      image_url: finalImageUrl
    };

    if (dish) {
      data.id = dish.id;
    }
    onSubmit(data);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Update image preview when image_url changes
    if (name === 'image_url') {
      setImagePreview(value);
      setImageError(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        customToast.error(toastMessages.imageFormatError);
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        customToast.error(toastMessages.imageSizeError);
        return;
      }

      setImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
        setImageError(false);
      };
      reader.readAsDataURL(file);

      // Clear URL input when file is selected
      setFormData(prev => ({ ...prev, image_url: '' }));
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const clearImage = () => {
    setImageFile(null);
    setFormData(prev => ({ ...prev, image_url: '' }));
    setImagePreview('');
    setImageError(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-2xl mx-auto my-8 shadow-2xl border border-gray-100">
        {/* Header */}
        <div className="p-4 md:p-6 border-b border-gray-100">
          <div className="flex items-center gap-3 md:gap-4">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg md:rounded-xl flex items-center justify-center text-white flex-shrink-0">
              <Utensils className="h-4 w-4 md:h-5 md:w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg md:text-xl font-bold text-gray-900 truncate">
                {dish
                  ? t('menuManagement.editDish', 'Edit Dish')
                  : t('menuManagement.createDish', 'Create Dish')
                }
              </h3>
              <p className="text-gray-600 text-sm md:text-base truncate">
                {dish
                  ? t('menuManagement.updateDishDescription', 'Update your dish details')
                  : t('menuManagement.createDishDescription', 'Add a new dish to your menu')
                }
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-300 flex-shrink-0"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4 md:space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
          {/* Image Upload Section */}
          <FeatureGate feature="advanced_menu">
            <div className="space-y-3 md:space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                {t('menuManagement.dishImage', 'Dish Image')}
              </label>

              <div className="flex flex-col sm:flex-row items-center gap-4 md:gap-6">
                {/* Image Preview */}
                <div className="flex-shrink-0">
                  {imagePreview && !imageError ? (
                    <div className="relative">
                      <img
                        src={imagePreview}
                        alt="Dish preview"
                        className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 object-cover rounded-xl border border-gray-200"
                        onError={handleImageError}
                      />
                      <button
                        type="button"
                        onClick={clearImage}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors duration-300"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center border-2 border-dashed border-gray-300">
                      <ImageIcon className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Upload Controls */}
                <div className="flex-1 min-w-0 space-y-3">
                  {/* File Upload */}
                  <div>
                    <label className="block w-full">
                      <span className="sr-only">{t('menuManagement.chooseDishImage', 'Choose dish image')}</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="block w-full text-sm text-gray-500
                          file:mr-3 file:py-2 file:px-3 
                          file:rounded-lg file:border-0 
                          file:text-xs file:font-medium
                          file:bg-blue-50 file:text-blue-700 
                          hover:file:bg-blue-100
                          file:cursor-pointer
                          cursor-pointer"
                      />
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">
                    {t('menuManagement.imageUploadHint', 'JPEG, PNG, WebP, GIF - Max 5MB')}
                  </p>
                </div>
              </div>
            </div>
          </FeatureGate>

          {/* Form Fields Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
            {/* Dish Name */}
            <div className="sm:col-span-2 space-y-2">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                {t('menuManagement.dishName', 'Dish Name')} *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="block w-full px-3 md:px-4 py-2.5 md:py-3 bg-white border border-gray-200 rounded-xl placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 hover:border-gray-300 text-sm md:text-base"
                placeholder={t('menuManagement.dishNamePlaceholder', 'Enter dish name')}
                required
              />
            </div>

            {/* Price */}
            <div className="space-y-2">
              <label htmlFor="price" className="block text-sm font-medium text-gray-700">
                {t('menuManagement.price', 'Price')} *
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="number"
                  id="price"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  className="block w-full pl-10 pr-3 md:pr-4 py-2.5 md:py-3 bg-white border border-gray-200 rounded-xl placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 hover:border-gray-300 text-sm md:text-base"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  required
                />
              </div>
            </div>

            {/* Category */}
            <div className="space-y-2">
              <label htmlFor="category_id" className="block text-sm font-medium text-gray-700">
                {t('menuManagement.category', 'Category')}
              </label>
              <select
                id="category_id"
                name="category_id"
                value={formData.category_id}
                onChange={handleChange}
                className="block w-full px-3 md:px-4 py-2.5 md:py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 hover:border-gray-300 text-sm md:text-base"
              >
                <option value="">{t('menuManagement.noCategory', 'No Category')}</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div className="sm:col-span-2 space-y-2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                {t('menuManagement.description', 'Description')}
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="block w-full px-3 md:px-4 py-2.5 md:py-3 bg-white border border-gray-200 rounded-xl placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 hover:border-gray-300 resize-none text-sm md:text-base"
                placeholder={t('menuManagement.descriptionPlaceholder', 'Enter dish description')}
                rows="3"
              />
            </div>

            {/* Availability */}
            <div className="sm:col-span-2 space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                {t('menuManagement.availability', 'Availability')}
              </label>
              <div className="flex items-center gap-3 p-3 md:p-4 bg-gray-50 rounded-xl border border-gray-200">
                <input
                  type="checkbox"
                  id="available"
                  name="available"
                  checked={formData.available}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="available" className="text-sm font-medium text-gray-700">
                  {t('menuManagement.availableForOrdering', 'Available for ordering')}
                </label>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="order-2 sm:order-1 px-4 md:px-6 py-2.5 md:py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all duration-300 border border-gray-200 text-sm md:text-base"
            >
              {t('menuManagement.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading || uploading}
              className="order-1 sm:order-2 px-4 md:px-6 py-2.5 md:py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2 text-sm md:text-base flex-1 sm:flex-none"
            >
              {(isLoading || uploading) ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {uploading
                    ? t('menuManagement.uploading', 'Uploading...')
                    : (dish
                      ? t('menuManagement.updating', 'Updating...')
                      : t('menuManagement.creating', 'Creating...')
                    )
                  }
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  {dish
                    ? t('menuManagement.updateDish', 'Update Dish')
                    : t('menuManagement.createDish', 'Create Dish')
                  }
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MenuManagement;